import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dumbbell, 
  Plus, 
  Sparkles, 
  Clock, 
  Loader2,
  ChevronRight,
  Play,
  Filter
} from 'lucide-react';

export default function WorkoutsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState([]);
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiParams, setAiParams] = useState({
    goal: 'strength',
    fitness_level: 'intermediate',
    duration_minutes: 45,
    equipment: 'full_gym'
  });
  const [filter, setFilter] = useState({ category: '', difficulty: '' });

  const fetchWorkouts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.category) params.append('category', filter.category);
      if (filter.difficulty) params.append('difficulty', filter.difficulty);
      
      const response = await axios.get(`${API}/workouts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkouts(response.data);
    } catch (error) {
      console.error('Error fetching workouts:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  const fetchMyWorkouts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/my-workouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyWorkouts(response.data);
    } catch (error) {
      console.error('Error fetching my workouts:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchWorkouts();
    fetchMyWorkouts();
  }, [fetchWorkouts, fetchMyWorkouts]);

  const generateAIWorkout = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/workouts/ai-generate`,
        null,
        {
          params: aiParams,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('AI workout generated!');
      setShowAIDialog(false);
      setMyWorkouts(prev => [response.data, ...prev]);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate workout';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="workouts-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            WORKOUT <span className="text-primary">PROGRAMS</span>
          </h1>
          <p className="text-gray-400 mt-1">Build strength and transform your body</p>
        </div>
        
        <div className="flex gap-3">
          <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
            <DialogTrigger asChild>
              <Button 
                data-testid="ai-workout-btn"
                className="bg-primary hover:bg-red-700 text-white uppercase font-semibold"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI Generate
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white uppercase">
                  AI Workout Builder
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Goal</Label>
                  <Select 
                    value={aiParams.goal} 
                    onValueChange={(v) => setAiParams({...aiParams, goal: v})}
                  >
                    <SelectTrigger className="bg-secondary border-transparent">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strength">Build Strength</SelectItem>
                      <SelectItem value="cardio">Cardio Endurance</SelectItem>
                      <SelectItem value="hiit">HIIT Fat Burn</SelectItem>
                      <SelectItem value="flexibility">Flexibility</SelectItem>
                      <SelectItem value="sports">Sports Performance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Fitness Level</Label>
                  <Select 
                    value={aiParams.fitness_level} 
                    onValueChange={(v) => setAiParams({...aiParams, fitness_level: v})}
                  >
                    <SelectTrigger className="bg-secondary border-transparent">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Duration (minutes)</Label>
                  <Select 
                    value={String(aiParams.duration_minutes)} 
                    onValueChange={(v) => setAiParams({...aiParams, duration_minutes: parseInt(v)})}
                  >
                    <SelectTrigger className="bg-secondary border-transparent">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20 min</SelectItem>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="45">45 min</SelectItem>
                      <SelectItem value="60">60 min</SelectItem>
                      <SelectItem value="90">90 min</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300">Equipment</Label>
                  <Select 
                    value={aiParams.equipment} 
                    onValueChange={(v) => setAiParams({...aiParams, equipment: v})}
                  >
                    <SelectTrigger className="bg-secondary border-transparent">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Equipment</SelectItem>
                      <SelectItem value="minimal">Minimal (Dumbbells)</SelectItem>
                      <SelectItem value="full_gym">Full Gym</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  data-testid="generate-workout-btn"
                  onClick={generateAIWorkout}
                  disabled={generating}
                  className="w-full bg-primary hover:bg-red-700 text-white uppercase font-semibold mt-4"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Workout
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="library" className="w-full">
        <TabsList className="bg-secondary/50 border border-border">
          <TabsTrigger value="library" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Workout Library
          </TabsTrigger>
          <TabsTrigger value="my-workouts" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            My Workouts
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="library" className="mt-6">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Select value={filter.category || "all"} onValueChange={(v) => setFilter({...filter, category: v === "all" ? "" : v})}>
              <SelectTrigger className="w-40 bg-secondary border-transparent">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="strength">Strength</SelectItem>
                <SelectItem value="cardio">Cardio</SelectItem>
                <SelectItem value="hiit">HIIT</SelectItem>
                <SelectItem value="yoga">Yoga</SelectItem>
                <SelectItem value="sports">Sports</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filter.difficulty || "all"} onValueChange={(v) => setFilter({...filter, difficulty: v === "all" ? "" : v})}>
              <SelectTrigger className="w-40 bg-secondary border-transparent">
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Workouts Grid */}
          {workouts.length === 0 ? (
            <div className="text-center py-12 card-tactical">
              <Dumbbell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">No workouts yet</h3>
              <p className="text-gray-400 mb-4">Generate your first AI workout to get started!</p>
              <Button 
                onClick={() => setShowAIDialog(true)}
                className="bg-primary hover:bg-red-700"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Workout
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {workouts.map((workout) => (
                <WorkoutCard key={workout.id} workout={workout} onStart={() => navigate(`/workouts/${workout.id}`)} />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="my-workouts" className="mt-6">
          {myWorkouts.length === 0 ? (
            <div className="text-center py-12 card-tactical">
              <Dumbbell className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">No custom workouts yet</h3>
              <p className="text-gray-400 mb-4">Create your own workout or generate one with AI!</p>
              <Button 
                onClick={() => setShowAIDialog(true)}
                className="bg-primary hover:bg-red-700"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Workout
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myWorkouts.map((workout) => (
                <WorkoutCard key={workout.id} workout={workout} isOwner onStart={() => navigate(`/workouts/${workout.id}`)} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const WorkoutCard = ({ workout, isOwner, onStart }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-500 bg-green-500/10';
      case 'intermediate': return 'text-yellow-500 bg-yellow-500/10';
      case 'advanced': return 'text-red-500 bg-red-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  return (
    <div className="card-tactical group cursor-pointer" onClick={onStart}>
      <div className="flex items-start justify-between mb-4">
        <div className={`px-2 py-1 rounded-sm text-xs uppercase font-semibold ${getDifficultyColor(workout.difficulty)}`}>
          {workout.difficulty}
        </div>
        {workout.is_ai_generated && (
          <div className="flex items-center gap-1 text-primary text-xs">
            <Sparkles className="w-3 h-3" />
            AI
          </div>
        )}
      </div>
      
      <h3 className="font-heading text-xl text-white mb-2">{workout.title}</h3>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{workout.description}</p>
      
      <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          {workout.duration_weeks} weeks
        </div>
        <div className="flex items-center gap-1">
          <Dumbbell className="w-4 h-4" />
          {workout.exercises?.length || 0} exercises
        </div>
      </div>
      
      <Button 
        data-testid={`start-workout-${workout.id}`}
        className="w-full bg-secondary hover:bg-primary group-hover:bg-primary transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          onStart();
        }}
      >
        <Play className="w-4 h-4 mr-2" />
        Start Workout
      </Button>
    </div>
  );
};
